import type { DirectiveBinding, ObjectDirective, ComponentPublicInstance } from 'vue'

// Custom directive for handling clicks outside an element

// 扩展ComponentPublicInstance类型，使其可以包含popperRef属性
interface CustomComponentPublicInstance extends ComponentPublicInstance {
  popperRef?: HTMLElement
}

interface ClickOutsideElement extends HTMLElement {
  _clickOutsideHandler?: (e: MouseEvent) => void
}

// 检查是否是DOM元素，并且包含contains方法
function isElement(target: any): target is HTMLElement {
  return target && typeof target === 'object' && target.nodeType === 1 && typeof target.contains === 'function'
}

const ClickOutside: ObjectDirective = {
  beforeMount(el: ClickOutsideElement, binding: DirectiveBinding) {
    // 创建事件处理函数
    const handler = (e: MouseEvent) => {
      const { value } = binding
      const customInstance = binding.instance as CustomComponentPublicInstance | undefined
      const popperRef = (binding.arg as unknown as HTMLElement) || customInstance?.popperRef

      // 确保popperRef是DOM元素并且有contains方法
      const isTargetExcluded = isElement(popperRef) && (popperRef === e.target || popperRef.contains(e.target as Node))

      // 如果点击的是元素本身或其子元素，或者是popperRef或其子元素，则不做任何处理
      if (el === e.target || el.contains(e.target as Node) || isTargetExcluded) {
        return
      }

      // 如果点击的是外部元素，并且有preventClick修饰符，则阻止事件传播
      if (binding.modifiers.preventClick) {
        e.stopPropagation()
        e.preventDefault()
      }

      // 调用绑定的函数
      if (typeof value === 'function') {
        value(e)
      }
    }

    // 使用捕获阶段监听点击事件，这样可以在事件到达目标元素之前就处理它
    document.addEventListener('click', handler, true)

    // 保存处理函数引用，以便在卸载时移除
    el._clickOutsideHandler = handler
  },

  updated(el: ClickOutsideElement, binding: DirectiveBinding) {
    // 如果处理函数已存在，先移除它
    if (el._clickOutsideHandler) {
      document.removeEventListener('click', el._clickOutsideHandler, true)
    }

    // 创建新的事件处理函数
    const handler = (e: MouseEvent) => {
      const { value } = binding
      const customInstance = binding.instance as CustomComponentPublicInstance | undefined
      const popperRef = (binding.arg as unknown as HTMLElement) || customInstance?.popperRef

      // 确保popperRef是DOM元素并且有contains方法
      const isTargetExcluded = isElement(popperRef) && (popperRef === e.target || popperRef.contains(e.target as Node))

      // 如果点击的是元素本身或其子元素，或者是popperRef或其子元素，则不做任何处理
      if (el === e.target || el.contains(e.target as Node) || isTargetExcluded) {
        return
      }

      // 如果点击的是外部元素，并且有preventClick修饰符，则阻止事件传播
      if (binding.modifiers.preventClick) {
        e.stopPropagation()
        e.preventDefault()
      }

      // 调用绑定的函数
      if (typeof value === 'function') {
        value(e)
      }
    }

    // 使用捕获阶段监听点击事件
    document.addEventListener('click', handler, true)

    // 保存处理函数引用
    el._clickOutsideHandler = handler
  },

  unmounted(el: ClickOutsideElement) {
    // 移除事件监听器
    if (el._clickOutsideHandler) {
      document.removeEventListener('click', el._clickOutsideHandler, true)
      delete el._clickOutsideHandler
    }
  }
}

export default ClickOutside
export const vClickOutside = ClickOutside
